import React, { useState } from 'react';
import { Printer } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useTranslation } from '../../hooks/useTranslation';
import { useNotification } from '../../hooks/useNotification';
import { ordersService } from '../../services/ordersService';
import { Order } from '../../types';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { usePaymentSettingsStore } from '../../stores/paymentSettingsStore';
import QRCode from 'qrcode';

interface PrintBillButtonProps {
  orderId: string;
}

// Cache for product names
const productNameCache = new Map<string, string>();

const fetchProductName = async (
  productId: string | number
): Promise<string> => {
  const productIdStr = String(productId);

  // Check cache first
  if (productNameCache.has(productIdStr)) {
    return productNameCache.get(productIdStr) || productIdStr;
  }

  try {
    const response = await authenticatedFetch(`/api/products/${productIdStr}`);
    const product = await response.json();
    const productName = product.name || productIdStr;
    productNameCache.set(productIdStr, productName);
    return productName;
  } catch (error) {
    console.error(`Failed to fetch product ${productIdStr}:`, error);
    return productIdStr;
  }
};

export const PrintBillButton: React.FC<PrintBillButtonProps> = ({
  orderId,
}) => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { formatCurrency } = useCurrencyFormat();
  const { settings: paymentSettings } = usePaymentSettingsStore();
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrintBill = async () => {
    try {
      setIsPrinting(true);

      // Fetch the order data
      const order = (await ordersService.getOrder(orderId)) as Order;

      // Fetch suborders if this is a parent order
      let suborders: Order[] = [];
      if (!order.parentOrderId) {
        try {
          // Fetch orders with pagination to ensure we get all suborders
          let orderSuborders: Order[] = [];
          let page = 1;
          const perPage = 1000;
          let hasMore = true;

          while (hasMore) {
            const ordersResponse = await ordersService.getOrders({
              pagination: { page, perPage },
              sort: { field: 'date', order: 'DESC' },
              filter: {},
            });

            const pageSuborders = (ordersResponse.data || []).filter(
              (o: Order) => o.parentOrderId === order.id
            );
            orderSuborders = orderSuborders.concat(pageSuborders);

            // Check if we've fetched all orders
            if (
              ordersResponse.data.length < perPage ||
              orderSuborders.length >= (ordersResponse.total || 0)
            ) {
              hasMore = false;
            } else {
              page++;
            }
          }

          suborders = orderSuborders;
        } catch (error) {
          console.error('Error fetching suborders for print:', error);
          // Continue without suborders if fetch fails
        }
      }

      // Combine all items: parent order items + all suborder items
      const allItems = [...(order.items || [])];
      const suborderItems: Array<{ item: any; suborderReference: string }> = [];
      
      suborders.forEach((suborder) => {
        (suborder.items || []).forEach((item) => {
          allItems.push(item);
          suborderItems.push({
            item,
            suborderReference: suborder.reference || `SUB-${suborder.id}`,
          });
        });
      });

      // Fetch product names for all items (with timeout to avoid hanging)
      const productNamePromises = allItems.map((item) =>
        Promise.race([
          fetchProductName(item.productId),
          new Promise<string>((resolve) =>
            setTimeout(() => resolve(String(item.productId)), 2000)
          ),
        ])
      );
      const productNames = await Promise.all(productNamePromises);

      // Calculate totals using the same logic as ModernOrderShow
      const items = allItems;

      // Calculate totalExTaxes from all items (parent + suborders)
      const calculatedTotalExTaxes = items.reduce((sum: number, item: any) => {
        const quantity = Number(item.quantity) || 0;
        const price = Number(item.price) || 0;
        return sum + quantity * price;
      }, 0);

      // Calculate suborder totals
      const suborderTotal = suborders.reduce((sum, suborder) => {
        return (
          sum +
          (suborder.items || []).reduce((itemSum: number, item: any) => {
            const quantity = Number(item.quantity) || 0;
            const price = Number(item.price) || 0;
            return itemSum + quantity * price;
          }, 0)
        );
      }, 0);

      const pointsRedemption = Number((order as any).pointsRedemption) || 0;
      const taxes = Number(order.taxes) || 0;
      const deliveryFees = Number(order.deliveryFees) || 0;
      // Use calculated total (includes suborders) instead of just order.totalExTaxes
      const totalExTaxes = calculatedTotalExTaxes || 0;

      // Calculate discount (promotion discount)
      // For parent orders with suborders, we need to calculate the combined total
      const orderTotal = Number(order.total) || 0;
      const combinedSuborderTotal = suborders.reduce((sum, suborder) => {
        return sum + (Number(suborder.total) || 0);
      }, 0);
      
      // Combined total includes parent order total + all suborder totals
      const combinedTotal = orderTotal + combinedSuborderTotal;
      
      let discount = 0;

      if (combinedTotal > 0 && totalExTaxes > 0) {
        // Reverse calculate: discount = totalExTaxes - (total - taxes - deliveryFees + pointsRedemption)
        const expectedSubtotal =
          combinedTotal - taxes - deliveryFees + pointsRedemption;
        if (totalExTaxes > expectedSubtotal) {
          discount = totalExTaxes - expectedSubtotal;
        }
      }

      // Use combined total (parent + suborders) if it exists and is valid (> 0), otherwise calculate from items
      let grandTotal = combinedTotal;

      // If combined total is 0 or missing, calculate it from items
      if (!grandTotal || grandTotal === 0) {
        // total = totalExTaxes + taxes + deliveryFees - discount
        grandTotal = totalExTaxes + taxes + deliveryFees - discount;
        // Ensure total is not negative
        grandTotal = Math.max(0, grandTotal);
      }

      // Recalculate discount: if totalExTaxes + taxes + deliveryFees > total, there's a discount
      // This accounts for both promotion discount and points redemption
      if (totalExTaxes > 0 && grandTotal > 0) {
        const totalBeforeDiscount = totalExTaxes + taxes + deliveryFees;
        if (totalBeforeDiscount > grandTotal) {
          discount = totalBeforeDiscount - grandTotal;
        }
      }

      const subTotal = totalExTaxes;
      const serviceFee = deliveryFees;
      const paid = order.paid ? grandTotal : 0;
      const change = paid - grandTotal;

      // Get QR code (either from uploaded image or generate from text data)
      let qrCodeContent = '';
      let isQRCodeImage = false;
      if (paymentSettings.includeInReceipt) {
        // Use uploaded image if available
        if (paymentSettings.qrCodeImage) {
          qrCodeContent = paymentSettings.qrCodeImage;
          isQRCodeImage = true;
        }
        // Otherwise generate from text data
        else if (paymentSettings.qrCodeData.trim()) {
          try {
            qrCodeContent = await QRCode.toString(paymentSettings.qrCodeData, {
              type: 'svg',
              width: 150,
              margin: 1,
            });
            isQRCodeImage = false;
          } catch (error) {
            console.error('Error generating QR code for receipt:', error);
          }
        }
      }

      // Create print window content
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        notify('Please allow popups to print the bill', { type: 'error' });
        return;
      }

      // Format date
      const orderDate = new Date(order.date);
      const formattedDate = orderDate.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      // Translate labels for receipt
      const pointsUsedLabel = translate('pos.orders.fields.pointsUsed');
      const discountLabel = translate('pos.orders.fields.discount');
      const serviceFeeLabel = translate('pos.orders.fields.serviceFee');
      const tableLabel = translate('pos.orders.fields.tableNumber');
      const receiptLabel = translate('pos.orders.fields.receipt');
      const orderLabel = translate('pos.orders.fields.order');
      const dateLabel = translate('pos.orders.fields.date');
      const typeLabel = translate('pos.orders.fields.type');
      const statusLabel = translate('pos.orders.fields.status');
      const noteLabel = translate('pos.orders.fields.note');
      const subtotalLabel = translate('pos.orders.fields.subtotal');
      const taxLabel = translate('pos.orders.fields.tax');
      const totalLabel = translate('pos.orders.fields.total');
      const paymentQRCodeLabel = translate('pos.orders.fields.paymentQRCode');

      // Build receipt HTML
      const receiptHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Receipt - ${order.reference}</title>
            <style>
              @media print {
                @page {
                  size: 80mm auto;
                  margin: 0;
                }
                body {
                  margin: 0;
                  padding: 10mm;
                }
              }
              body {
                font-family: 'Courier New', monospace;
                font-size: 12px;
                line-height: 1.4;
                max-width: 80mm;
                margin: 0 auto;
                padding: 10mm;
              }
              .header {
                text-align: center;
                margin-bottom: 15px;
                border-bottom: 1px dashed #000;
                padding-bottom: 10px;
              }
              .store-name {
                font-size: 16px;
                font-weight: bold;
                margin-bottom: 5px;
              }
              .order-info {
                margin: 10px 0;
                font-size: 11px;
              }
              .order-info div {
                margin: 3px 0;
              }
              .items {
                margin: 15px 0;
                border-top: 1px dashed #000;
                border-bottom: 1px dashed #000;
                padding: 10px 0;
              }
              .item {
                margin: 8px 0;
              }
              .item-name {
                font-weight: bold;
              }
              .item-details {
                display: flex;
                justify-content: space-between;
                margin-top: 3px;
                font-size: 11px;
              }
              .item-note {
                font-style: italic;
                color: #666;
                font-size: 10px;
                margin-top: 2px;
              }
              .totals {
                margin: 15px 0;
              }
              .total-row {
                display: flex;
                justify-content: space-between;
                margin: 5px 0;
                font-size: 11px;
              }
              .total-row.grand-total {
                font-weight: bold;
                font-size: 14px;
                border-top: 1px solid #000;
                border-bottom: 1px solid #000;
                padding: 8px 0;
                margin: 10px 0;
              }
              .footer {
                text-align: center;
                margin-top: 20px;
                padding-top: 10px;
                border-top: 1px dashed #000;
                font-size: 10px;
              }
              .qr-code-container {
                margin-top: 15px;
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 5px;
              }
              .qr-code-label {
                font-weight: bold;
                font-size: 11px;
                margin-bottom: 5px;
              }
              .qr-code {
                background: white;
                padding: 5px;
                border: 1px solid #ddd;
              }
              .qr-code img {
                max-width: 150px;
                max-height: 150px;
                display: block;
                filter: grayscale(100%);
              }
              .status {
                text-align: center;
                margin: 10px 0;
                padding: 5px;
                font-weight: bold;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="store-name">NEKO COFFEE</div>
              <div style="font-size: 10px;">${receiptLabel}</div>
            </div>
            
            <div class="order-info">
              <div><strong>${orderLabel}:</strong> ${order.reference}</div>
              <div><strong>${dateLabel}:</strong> ${formattedDate}</div>
              ${
                order.tableNumber
                  ? `<div><strong>${tableLabel}:</strong> ${order.tableNumber}</div>`
                  : ''
              }
              ${
                order.orderType
                  ? `<div><strong>${typeLabel}:</strong> ${order.orderType
                      .replace('_', ' ')
                      .toUpperCase()}</div>`
                  : ''
              }
              <div class="status">${statusLabel}: ${order.status.toUpperCase()}</div>
            </div>

            <div class="items">
              ${(() => {
                let itemIndex = 0;
                let html = '';
                
                // Parent order items
                order.items.forEach((item) => {
                  html += `
                    <div class="item">
                      <div class="item-name">${
                        productNames[itemIndex] || item.productId
                      } × ${item.quantity}</div>
                      <div class="item-details">
                        <span>${formatCurrency(item.price)} × ${
                        item.quantity
                      }</span>
                        <span>${formatCurrency(item.price * item.quantity)}</span>
                      </div>
                      ${
                        item.note
                          ? `<div class="item-note">${noteLabel}: ${item.note}</div>`
                          : ''
                      }
                    </div>
                  `;
                  itemIndex++;
                });
                
                // Suborder items
                suborders.forEach((suborder) => {
                  html += `
                    <div class="item" style="border-top: 1px dashed #ccc; margin-top: 10px; padding-top: 10px;">
                      <div style="font-weight: bold; font-size: 11px; color: #666; margin-bottom: 5px;">
                        ${suborder.reference || `SUB-${suborder.id}`}
                      </div>
                  `;
                  
                  (suborder.items || []).forEach((item) => {
                    html += `
                      <div class="item" style="margin-left: 10px;">
                        <div class="item-name">${
                          productNames[itemIndex] || item.productId
                        } × ${item.quantity}</div>
                        <div class="item-details">
                          <span>${formatCurrency(item.price)} × ${
                          item.quantity
                        }</span>
                          <span>${formatCurrency(item.price * item.quantity)}</span>
                        </div>
                        ${
                          item.note
                            ? `<div class="item-note">${noteLabel}: ${item.note}</div>`
                            : ''
                        }
                      </div>
                    `;
                    itemIndex++;
                  });
                  
                  html += `</div>`;
                });
                
                return html;
              })()}
            </div>

            <div class="totals">
              <div class="total-row">
                <span>${subtotalLabel}:</span>
                <span>${formatCurrency(subTotal)}</span>
              </div>
              ${
                pointsRedemption > 0
                  ? `
              <div class="total-row">
                <span>${pointsUsedLabel}:</span>
                <span>-${formatCurrency(pointsRedemption)}</span>
              </div>
              `
                  : ''
              }
              ${
                discount > 0
                  ? `
              <div class="total-row">
                <span>${discountLabel}:</span>
                <span>-${formatCurrency(discount)}</span>
              </div>
              `
                  : ''
              }
              ${
                serviceFee > 0
                  ? `
              <div class="total-row">
                <span>${serviceFeeLabel}:</span>
                <span>${formatCurrency(serviceFee)}</span>
              </div>
              `
                  : ''
              }
              ${
                taxes > 0
                  ? `
              <div class="total-row">
                <span>${taxLabel}:</span>
                <span>${formatCurrency(taxes)}</span>
              </div>
              `
                  : ''
              }
              <div class="total-row grand-total">
                <span>${totalLabel.toUpperCase()}:</span>
                <span>${formatCurrency(grandTotal)}</span>
              </div>
              ${
                paid > 0
                  ? `
              <div class="total-row">
                <span>Paid:</span>
                <span>${formatCurrency(paid)}</span>
              </div>
              <div class="total-row">
                <span>Change:</span>
                <span>${formatCurrency(change)}</span>
              </div>
              `
                  : ''
              }
            </div>

            <div class="footer">
              <div>Thank you for your visit!</div>
              <div style="margin-top: 5px;">Have a great day!</div>
              ${
                qrCodeContent && paymentSettings.includeInReceipt
                  ? `
              <div class="qr-code-container">
                ${
                  paymentSettings.qrCodeLabel
                    ? `<div class="qr-code-label">${paymentSettings.qrCodeLabel}</div>`
                    : ''
                }
                <div class="qr-code">${
                  isQRCodeImage
                    ? `<img src="${qrCodeContent}" alt="${paymentQRCodeLabel}" />`
                    : qrCodeContent
                }</div>
              </div>
              `
                  : ''
              }
            </div>
          </body>
        </html>
      `;

      printWindow.document.write(receiptHTML);
      printWindow.document.close();

      // Wait for content to load, then trigger print
      // Use a small delay to ensure content is fully rendered
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        // Close the window after printing (optional)
        setTimeout(() => {
          printWindow.close();
        }, 100);
      }, 250);

      notify('Print dialog opened', { type: 'success' });
    } catch (error) {
      console.error('Error printing bill:', error);
      notify('Failed to print bill. Please try again.', { type: 'error' });
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handlePrintBill}
      disabled={isPrinting}
      className="flex items-center gap-2"
    >
      <Printer className="h-4 w-4" />
      {isPrinting
        ? translate('pos.orders.actions.printing', 'Printing...')
        : translate('pos.orders.actions.printBill')}
    </Button>
  );
};
